import { createTheme, ThemeOptions, PaletteOptions } from '@mui/material/styles'
import { ThemeName, ThemeRegistry } from './types'

/**
 * Base theme configuration shared across all VeilStream themes.
 * Surfaces and semantics are driven by CSS tokens in foundations/tokens/tokens.css.
 *
 * Canonical theme: **Evergreen Dark**. Other variants only change primary.main
 * for accent differentiation (see palettes below).
 */
const baseThemeOptions: Omit<ThemeOptions, 'palette'> = {
  typography: {
    fontFamily: [
      'InterVariable',
      'Inter',
      'system-ui',
      '-apple-system',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'Noto Sans',
      'Liberation Sans',
    ].join(','),
    h1: { fontWeight: 400 },
    h2: { fontWeight: 400, fontSize: '2.5rem', letterSpacing: '-0.015em' },
    h3: { fontWeight: 500 },
    h4: { fontWeight: 500, fontSize: '2.125rem' },
    h5: { fontWeight: 500 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
    overline: {
      fontSize: '0.65rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'var(--vs-bg)',
          color: 'var(--vs-text-primary)',
          fontFamily: 'var(--font-sans)',
        },
        // Olive focus ring — brand affirmative. Inputs also get a green border
        // via MuiOutlinedInput; this covers buttons, links, custom controls.
        '*:focus-visible': {
          outline: 'none',
          boxShadow: 'var(--vs-focus-ring)',
        },
        // Avatar photos are square files clipped to a circle; a focus/selection
        // ring on the <img> draws a rectangle around the circular portrait.
        'img:focus, img:focus-visible, .MuiAvatar-root:focus-visible, .MuiAvatar-img:focus-visible': {
          outline: 'none',
          boxShadow: 'none',
        },
        '.MuiAvatar-img': {
          pointerEvents: 'none',
          userSelect: 'none',
          WebkitUserDrag: 'none',
        },
        // Avoid double rings on MUI focused inputs (border already signals focus).
        '.MuiOutlinedInput-root.Mui-focused': {
          boxShadow: 'none',
        },
        '.MuiOutlinedInput-root.Mui-focused *:focus-visible': {
          boxShadow: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: { color: 'var(--vs-text-primary)' },
        h2: { color: 'var(--vs-text-primary)' },
        h3: { color: 'var(--vs-text-primary)' },
        h4: { color: 'var(--vs-text-primary)' },
        h5: { color: 'var(--vs-text-primary)' },
        h6: { color: 'var(--vs-text-primary)' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: 'var(--border)',
          backgroundColor: 'var(--vs-surface)',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-sm)',
          paddingInline: 16,
          paddingBlock: 10,
          transition: `background-color var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard)`,
        },
        containedPrimary: {
          backgroundColor: 'var(--vs-action)',
          color: 'var(--vs-on-action)',
          ':hover': {
            backgroundColor: 'var(--vs-action-hover)',
            filter: 'none',
          },
        },
        outlined: {
          borderColor: 'var(--vs-outline)',
          color: 'var(--vs-on-outline)',
          '&:hover': {
            borderColor: 'var(--vs-outline-hover)',
            backgroundColor: 'var(--vs-accent-wash)',
          },
        },
        outlinedPrimary: {
          borderColor: 'var(--vs-outline)',
          color: 'var(--vs-on-outline)',
          '&:hover': {
            borderColor: 'var(--vs-outline-hover)',
            backgroundColor: 'var(--vs-accent-wash)',
          },
        },
        outlinedSecondary: {
          borderColor: 'rgba(143, 167, 60, 0.85)',
          color: '#F7FAF0',
          '&:hover': {
            borderColor: 'rgba(167, 190, 90, 1)',
            backgroundColor: 'rgba(143, 167, 60, 0.14)',
          },
        },
        outlinedError: {
          borderColor: 'var(--vs-error)',
          color: 'var(--vs-error)',
          backgroundColor: 'var(--vs-error-bg)',
          '&:hover': {
            borderColor: 'var(--vs-error-soft)',
            backgroundColor: 'var(--vs-error-bg)',
          },
        },
        containedError: {
          backgroundColor: 'var(--vs-error)',
          color: 'var(--vs-on-action)',
          '&:hover': {
            backgroundColor: 'var(--palette-warning-400)',
          },
        },
        text: {
          color: 'var(--vs-text-secondary)',
          '&:hover': { backgroundColor: 'var(--vs-wash)' },
        },
        textPrimary: {
          color: 'var(--vs-link)',
          '&:hover': { backgroundColor: 'var(--vs-accent-wash)' },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'var(--vs-text-secondary)',
          '&:hover': { color: 'var(--vs-text-primary)' },
          '&.Mui-selected': {
            color: 'var(--vs-text-primary)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'var(--vs-link)',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: 'var(--vs-text-secondary)',
          borderColor: 'var(--vs-border)',
          '&.Mui-selected': {
            color: 'var(--vs-text-primary)',
            backgroundColor: 'var(--vs-wash-strong)',
            '&:hover': { backgroundColor: 'var(--vs-wash-strong)' },
          },
          '&:hover': {
            backgroundColor: 'var(--vs-wash)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--vs-surface)',
          borderBottom: 'var(--border)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--vs-surface)',
          border: 'var(--border)',
          borderRadius: 'var(--radius-md)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-sm)',
          fontWeight: 500,
        },
        // Filled chips on navy: tinted surface + brand label (avoid dark text on pale fill).
        colorSuccess: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(143, 167, 60, 0.18)',
            color: 'var(--vs-success)',
            border: '1px solid rgba(143, 167, 60, 0.45)',
          },
          '&.MuiChip-outlined': {
            borderColor: 'rgba(143, 167, 60, 0.55)',
            color: 'var(--vs-success)',
          },
        },
        colorWarning: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(165, 112, 48, 0.18)',
            color: 'var(--vs-pending)',
            border: '1px solid rgba(165, 112, 48, 0.45)',
          },
          '&.MuiChip-outlined': {
            borderColor: 'rgba(165, 112, 48, 0.55)',
            color: 'var(--vs-pending)',
          },
        },
        colorError: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(172, 63, 54, 0.18)',
            color: 'var(--vs-log-error)',
            border: '1px solid rgba(172, 63, 54, 0.5)',
          },
          '&.MuiChip-outlined': {
            borderColor: 'rgba(232, 143, 136, 0.65)',
            color: 'var(--vs-log-error)',
          },
        },
        colorInfo: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(65, 135, 181, 0.18)',
            color: 'var(--vs-link)',
            border: '1px solid rgba(65, 135, 181, 0.45)',
          },
          '&.MuiChip-outlined': {
            borderColor: 'rgba(134, 190, 223, 0.45)',
            color: 'var(--vs-link)',
          },
        },
        colorPrimary: {
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(134, 190, 223, 0.16)',
            color: 'var(--vs-link)',
            border: '1px solid rgba(134, 190, 223, 0.45)',
          },
          '&.MuiChip-outlined': {
            borderColor: 'rgba(134, 190, 223, 0.55)',
            color: 'var(--vs-link)',
            backgroundColor: 'transparent',
          },
        },
        outlined: {
          borderColor: 'var(--vs-border)',
          color: 'var(--vs-text-secondary)',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'var(--vs-link)',
          ':hover': { color: 'var(--palette-primary-800)' },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--vs-surface)',
            borderRadius: 'var(--radius-md)',
            '& fieldset': { borderColor: 'var(--vs-border)' },
            '&:hover fieldset': { borderColor: 'var(--vs-border-hover)' },
            '&.Mui-focused fieldset': { borderColor: 'var(--vs-accent)' },
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        img: {
          pointerEvents: 'none',
          userSelect: 'none',
          WebkitUserDrag: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--vs-surface-raised)',
          color: 'var(--vs-text-primary)',
          border: 'var(--border)',
          boxShadow: 'var(--shadow-md)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { color: 'var(--vs-text-secondary)', borderWidth: 1, borderStyle: 'solid' },
        standardSuccess: {
          backgroundColor: 'var(--vs-success-bg)',
          borderColor: 'var(--palette-secondary1-100)',
          color: 'var(--vs-success)',
          '& .MuiAlert-icon': { color: 'var(--vs-accent)' },
        },
        standardError: {
          backgroundColor: 'var(--vs-error-bg)',
          borderColor: 'var(--palette-warning-900)',
          color: 'var(--vs-log-error)',
          '& .MuiAlert-icon': { color: 'var(--vs-error)' },
        },
        standardWarning: {
          backgroundColor: 'var(--vs-warning-bg)',
          borderColor: 'var(--palette-secondary2-900)',
          color: 'var(--vs-pending)',
          '& .MuiAlert-icon': { color: 'var(--vs-warning)' },
        },
        standardInfo: {
          backgroundColor: 'var(--vs-info-bg)',
          borderColor: 'var(--palette-primary-100)',
          color: 'var(--vs-link)',
          '& .MuiAlert-icon': { color: 'var(--vs-info)' },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--vs-surface-raised)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--vs-surface-raised)',
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--vs-surface-raised)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--vs-surface)',
          padding: '8px 16px',
          '&:nth-of-type(even)': {
            backgroundColor: 'var(--vs-surface)',
          },
          '&:nth-of-type(odd)': {
            backgroundColor: 'var(--vs-surface)',
          },
          '&:hover': {
            backgroundColor: 'var(--vs-wash)',
            color: 'var(--vs-text-primary)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          border: 'none',
          padding: '8px 16px',
          fontSize: '0.875rem',
          color: 'var(--vs-text-primary)',
        },
        head: {
          backgroundColor: 'var(--vs-surface-raised)',
          color: 'var(--vs-text-primary)',
          fontWeight: 600,
          fontSize: '0.875rem',
        },
      },
    },
  },
}

/** Shared Evergreen-scale semantics (status, surfaces, text hierarchy). */
const evergreenDarkSemantics: Omit<PaletteOptions, 'primary'> = {
  mode: 'dark',
  secondary: { main: '#8FA73C' },
  success: { main: '#8FA73C', light: '#AFC662', dark: '#70891A' },
  warning: { main: '#A57030', light: '#E3BC83', dark: '#86561C' },
  error: { main: '#AC3F36', light: '#E88F88', dark: '#8B1F16' },
  info: { main: '#4187B5' },
  background: { default: '#001123', paper: '#001A36' },
  text: {
    primary: '#FFFFFF',
    secondary: 'rgba(255, 255, 255, 0.70)',
    disabled: 'rgba(255, 255, 255, 0.38)',
  },
  divider: '#002F61',
}

const evergreenLightSemantics: Omit<PaletteOptions, 'primary'> = {
  mode: 'light',
  secondary: { main: '#8FA73C' },
  success: { main: '#70891A', light: '#8FA73C', dark: '#546104' },
  warning: { main: '#86561C', light: '#A57030', dark: '#65400E' },
  error: { main: '#8B1F16', light: '#AC3F36', dark: '#6A0F08' },
  info: { main: '#014787' },
  background: { default: '#EEF3F8', paper: '#FFFFFF' },
  text: {
    primary: '#001123',
    secondary: 'rgba(0, 17, 35, 0.72)',
    disabled: 'rgba(0, 17, 35, 0.38)',
  },
  divider: '#C9D7E4',
}

function semanticsForMode(mode: 'light' | 'dark'): Omit<PaletteOptions, 'primary'> {
  return mode === 'light' ? evergreenLightSemantics : evergreenDarkSemantics
}

/**
 * Color palettes for each differentiated theme variant.
 * Culled clones (Midnight Maritime, Berry Noir, Graphite Pro) — Evergreen Dark
 * is canonical.
 */
const primaries: Record<ThemeName, PaletteOptions['primary']> = {
  'Evergreen Dark': { main: '#014787', light: '#4187B5', dark: '#002F61' },
  'Nord Mist': { main: '#86BEDF', light: '#A8D3EB', dark: '#4187B5' },
  'Desert Dusk': { main: '#A57030', light: '#C99955', dark: '#86561C' },
  'Neon Terminal': { main: '#8FA73C', light: '#AFC662', dark: '#70891A' },
  'Solar Ink': { main: '#4187B5', light: '#63A3CA', dark: '#22689E' },
}

/**
 * Get a theme by name and color mode. Mode defaults to dark (canonical).
 */
export function getTheme(name: ThemeName, mode: 'light' | 'dark' = 'dark') {
  return createTheme({
    ...baseThemeOptions,
    palette: {
      ...semanticsForMode(mode),
      primary: primaries[name],
    },
  })
}

/**
 * Registry of all available VeilStream themes (dark mode).
 */
export const themes: ThemeRegistry = Object.fromEntries(
  (Object.keys(primaries) as ThemeName[]).map((name) => [name, getTheme(name, 'dark')])
) as ThemeRegistry

/**
 * List of all available theme names
 */
export const themeNames = Object.keys(themes) as ThemeName[]

/**
 * Default / canonical theme name
 */
export const DEFAULT_THEME_NAME: ThemeName = 'Evergreen Dark'

/**
 * Default storage key for theme persistence
 */
export const DEFAULT_THEME_STORAGE_KEY = 'veilstream-theme'
